import { useState, useEffect, useRef } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { Input } from '@rocco/components/input';
import { StepContent } from '../StepContent';
import { HdeOptionList } from '../HDEOptionList';
import { COMPANY_TYPE } from '../../../utils/form-options';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
} from '../hde-option-styles';

interface CompanyTypeData {
    companyType: string | null;
    companyTypeOther?: string;
}

/**
 * Step for Professional and Exhibitor registration.
 * Collects company type selection.
 * When "Other" is selected, shows a "Please specify" input (same behaviour as attendance reason).
 */
export function CompanyTypeStep({
    setIsValid,
    onDataChange,
    onNext,
    initialData,
}: StepContentProps) {
    const data = initialData as CompanyTypeData | undefined;

    const [companyType, setCompanyType] = useState<string | null>(
        data?.companyType ?? null,
    );
    const [companyTypeOther, setCompanyTypeOther] = useState(
        data?.companyTypeOther ?? '',
    );
    const companyTypeOtherInputRef = useRef<HTMLInputElement>(null);

    const needCompanyTypeOther = companyType === 'Other';

    useEffect(() => {
        if (needCompanyTypeOther) {
            companyTypeOtherInputRef.current?.focus();
        }
    }, [needCompanyTypeOther]);

    const isValid =
        companyType !== null &&
        (!needCompanyTypeOther || companyTypeOther.trim().length > 0);

    useEffect(() => {
        setIsValid(isValid);
        onDataChange?.({
            companyType,
            companyTypeOther: needCompanyTypeOther ? companyTypeOther : '',
        });
    }, [
        companyType,
        companyTypeOther,
        needCompanyTypeOther,
        isValid,
        setIsValid,
        onDataChange,
    ]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StepContent>
                <div className="space-y-3">
                    <HdeOptionList
                        label="What industry is your company in?"
                        mode="single"
                        appearance="button"
                        options={COMPANY_TYPE}
                        value={companyType}
                        onChange={(value) => {
                            setCompanyType(value);
                            if (value !== 'Other') {
                                setCompanyTypeOther('');
                                onDataChange?.({
                                    companyType: value,
                                    companyTypeOther: '',
                                });
                            }
                        }}
                        onSelectAndAdvance={(value) => {
                            if (value !== 'Other') onNext();
                        }}
                        contentAfterOption={
                            needCompanyTypeOther
                                ? {
                                      optionValue: 'Other',
                                      content: (
                                          <div className="space-y-3">
                                              <label
                                                  className={getHdeFieldLabelClassName()}
                                              >
                                                  Please specify *
                                              </label>
                                              <Input
                                                  ref={companyTypeOtherInputRef}
                                                  value={companyTypeOther}
                                                  onChange={(e) =>
                                                      setCompanyTypeOther(
                                                          e.target.value,
                                                      )
                                                  }
                                                  placeholder="Please specify"
                                                  className={getHdeInputClassName()}
                                              />
                                          </div>
                                      ),
                                  }
                                : undefined
                        }
                    />
                </div>
            </StepContent>
        </div>
    );
}
